import React, { useContext, useLayoutEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { UMLoader } from '@urbanmetrix/um-kit';
import { AuthContext } from './authContext';
import { captureSubscriptionEntry, isSubscriptionEntry } from '../lib/pending-subscription';

type PrivateRouteProps = {
  children: React.ReactNode;
  permission?: string | string[];
  requireActivePlan?: boolean;
  requireNoActivePlan?: boolean;
};

export function PrivateRoute({
  children,
  permission,
  requireActivePlan,
  requireNoActivePlan,
}: PrivateRouteProps): React.ReactElement {
  const {
    user,
    permission: userPermissions,
    isAuthenticated,
    isLoading,
    isHydratingUser,
    hasBootstrappedSession,
  } = useContext(AuthContext);
  const location = useLocation();
  const path = location.pathname;
  const search = location.search;

  useLayoutEffect(() => {
    if (path === '/account/organization') {
      captureSubscriptionEntry(search);
    }
  }, [path, search]);

  const hasPermission = () => {
    if (!permission || !userPermissions) return true;
    const required = Array.isArray(permission) ? permission : [permission];
    return required.some((value) => userPermissions[value as keyof typeof userPermissions] === true);
  };
  const canAccessSubscriptionWithoutPlan = () => userPermissions?.owner === true;
  const hasActivePlan = () => user?.plan === true;
  const publicAuthRoutes = [
    '/login',
    '/register/verify',
    '/resetpassword',
    '/accept-invitation',
    '/email-confirmed',
    '/maintenance',
  ];
  const guestOnlyRoutes = ['/login'];

  // Maintenance is a public outage-status page and must not wait for the
  // unavailable authentication backend to bootstrap a session.
  if (path === '/maintenance') {
    return React.createElement(React.Fragment, null, children);
  }

  const getAuthenticatedLandingRoute = () => '/dashboard';

  if (!hasBootstrappedSession || isLoading || isHydratingUser) {
    return React.createElement('div', { className: 'auth-loader-screen' }, React.createElement(UMLoader));
  }

  if (!isAuthenticated && publicAuthRoutes.includes(path)) {
    return React.createElement(React.Fragment, null, children);
  }

  if (!isAuthenticated) {
    const subscriptionEntry = path === '/account/organization' && isSubscriptionEntry(search);

    return React.createElement(Navigate, {
      to: subscriptionEntry ? '/login?view=register' : '/login',
    });
  }

  if (guestOnlyRoutes.includes(path)) {
    return React.createElement(Navigate, { to: getAuthenticatedLandingRoute() });
  }

  if (path === '/accept-invitation') {
    return React.createElement(React.Fragment, null, children);
  }

  if (hasPermission()) {
    if (path === '/account/subscription' && !hasActivePlan() && !canAccessSubscriptionWithoutPlan()) {
      return React.createElement(Navigate, { to: '/account' });
    }

    if (requireActivePlan && !hasActivePlan()) {
      return React.createElement(Navigate, { to: '/dashboard' });
    }

    if (requireNoActivePlan && hasActivePlan()) {
      return React.createElement(Navigate, { to: '/dashboard' });
    }

    return React.createElement(React.Fragment, null, children);
  }

  const fallbackRoute = getAuthenticatedLandingRoute();

  if (fallbackRoute !== path) {
    return React.createElement(Navigate, { to: fallbackRoute });
  }

  return React.createElement(React.Fragment, null, children);
}
